/**
 * Storage grants (EHQ-427).
 *
 * A keyless (`ServerIdentity`) object-storage connection uses Enlighten's own
 * service account, so a project admin first asks for a bucket, or a prefix in
 * it, and an instance admin approves it in ehq-admin. A connection then names
 * the grant it uses, and its bucket and prefix must lie inside it.
 *
 * Server shapes are `rename_all = "camelCase"`; see
 * `server/src/api/projects/storage_grant_handlers.rs`.
 */
import { ObjectStorageProvider } from './object-storage.interface';

export type StorageGrantStatus = 'Pending' | 'Approved' | 'Rejected' | 'Revoked' | 'Withdrawn';

export interface StorageGrant {
  storageGrantId: number;
  projectId: number;
  provider: ObjectStorageProvider;
  bucket: string;
  /** `''` (the whole bucket) or `'segment/.../'`. */
  prefix: string;
  status: StorageGrantStatus;
  requestNote: string;
  decisionNote: string | null;
  requestedBy: number | null;
  decidedBy: number | null;
  requestedAt: string;
  decidedAt: string | null;
  endedAt: string | null;
}

export interface StorageGrantRequest {
  /** GCS only for now. */
  provider: ObjectStorageProvider;
  bucket: string;
  /** Empty or absent asks for the whole bucket. */
  prefix?: string;
  /** Why the project needs it, for the instance admin. */
  note?: string;
}

/** Most pending requests a project may have at once (server-enforced). */
export const MAX_PENDING_STORAGE_GRANTS = 5;

/**
 * A key prefix as the server stores it: no leading slash, and `''` or ending
 * in exactly one `/`. Mirrors ehq_core's `normalize_key_prefix`.
 */
export function normalizeKeyPrefix(prefix: string | null | undefined): string {
  const trimmed = (prefix ?? '').trim().replace(/^\/+|\/+$/g, '');
  return trimmed ? `${trimmed}/` : '';
}

/** Whether `prefix` (any form) lies inside the grant: its prefix or anything under it. */
export function grantCovers(grant: Pick<StorageGrant, 'prefix'>, prefix: string | null | undefined): boolean {
  return normalizeKeyPrefix(prefix).startsWith(grant.prefix);
}

/** `gs://bucket/prefix/`, as the server and ehq-admin show it. */
export function grantLocation(grant: Pick<StorageGrant, 'provider' | 'bucket' | 'prefix'>): string {
  const scheme: Record<string, string> = { GCS: 'gs', S3: 's3', AWS: 's3', Azure: 'az' };
  return `${scheme[grant.provider] ?? grant.provider.toLowerCase()}://${grant.bucket}/${grant.prefix}`;
}

/**
 * GCS bucket naming, as the server checks it: 3-63 characters (up to 222 when
 * dotted), lowercase letters, digits, `-`, `_` and `.`, starting and ending
 * with a letter or digit.
 */
export function isValidGcsBucketName(name: string): boolean {
  const max = name.includes('.') ? 222 : 63;
  return name.length >= 3 && name.length <= max && /^[a-z0-9][a-z0-9._-]*[a-z0-9]$/.test(name);
}
